import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StationRequestsTableProps, StationRequestV2 } from '../../../types/dashboardV2';
import { CopyIdButton } from '../../../components/common/CopyIdButton/CopyIdButton';
import {
  DataTable,
  type DataTableColumnMeta,
  type DataTableRenderers,
} from '../components/DataTable';
import { DateTimeCell, IdWithCopyCell } from '../components/cells';

/** Column shares below are shares of this; narrower viewports scroll the table instead. */
export const STATION_REQUESTS_TABLE_MIN_WIDTH = '1120px';

export const STATION_REQUESTS_COLUMNS = [
  { key: 'phone', labelKey: 'dashboard.stationRequests.table.phone', width: '17.5%', skeletonVariant: 'id' },
  { key: 'city', labelKey: 'dashboard.stationRequests.table.city', width: '19%', skeletonVariant: 'id' },
  { key: 'comment', labelKey: 'dashboard.stationRequests.table.comment', width: '25.5%', skeletonVariant: 'address' },
  { key: 'submittedBy', labelKey: 'dashboard.stationRequests.table.submittedBy', width: '20.5%', skeletonVariant: 'address' },
  { key: 'submittedAt', labelKey: 'dashboard.stationRequests.table.submittedAt', width: '9.5%', align: 'right', skeletonVariant: 'id' },
  { key: 'actions', labelKey: 'dashboard.tables.actions', width: '8%', align: 'center', skeletonVariant: 'action' },
] as const satisfies readonly DataTableColumnMeta[];

export const StationRequestsTable = memo(function StationRequestsTable({
  requests,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: StationRequestsTableProps) {
  const { t } = useTranslation();

  const renderers = useMemo<DataTableRenderers<StationRequestV2, typeof STATION_REQUESTS_COLUMNS>>(
    () => ({
      phone: (r) => r.phone,
      city: (r) => r.city,
      comment: (r) => (
        <span
          title={r.comment || ''}
          style={{ display: 'block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {r.comment || '—'}
        </span>
      ),
      submittedBy: (r) =>
        r.userEmail ? (
          <IdWithCopyCell
            value={r.userEmail}
            copyLabel={t('dashboard.stationRequests.table.copyEmail')}
          />
        ) : (
          <IdWithCopyCell
            value={r.userId}
            copyLabel={t('dashboard.stationRequests.table.copyId')}
            emptyText="—"
          />
        ),
      submittedAt: (r) => <DateTimeCell value={r.createdAt} />,
      actions: (r) => (
        <CopyIdButton
          value={r.id}
          label={t('dashboard.stationRequests.table.copyId')}
          variant="icon"
        />
      ),
    }),
    [t],
  );

  return (
    <DataTable
      columns={STATION_REQUESTS_COLUMNS}
      renderers={renderers}
      rows={requests}
      rowKey={(r) => r.id}
      minWidth={STATION_REQUESTS_TABLE_MIN_WIDTH}
      dense
      pagination={{ total, page, rowsPerPage, onPageChange, onRowsPerPageChange }}
    />
  );
});

export default StationRequestsTable;
